/*
 * File: WarehouseInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 18 May 2021 4:44:35 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Select, Space, Spin, Typography } from 'antd';
import { CreateSalesOrderType } from 'common/enums/CreateSalesOrderType';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetWarehousesQuery } from 'redux/services/useKingstonLivingstonService';
import { BlockLoader } from '../BlockLoader';
const { Title } = Typography;

/* ****************** Interfaces ****************** */
const styles: InlineStylesModel = {
  select: {
    width: `100%`
  },
  formItemCol: {
    marginRight: '16px',
    maxHeight: '42px'
  },
  titleColor: {
    color: '#2F5596'
  }
};

export const WarehouseInput: FC = () => {
  const {
    values: { warehouseId, siteId },
    setFieldValue
  } = useFormikContext<SalesOrderPayloadModel>();
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  /* ****************** Hook API ****************** */
  const { data: warehousesResponse, isFetching, isLoading } = useGetWarehousesQuery({ warehouseContains: inputValue, site: siteId, offset, dataAreaId, overrideSkipTake: true }, { skip: !siteId });

  /* ****************** JSX / Variables ****************** */
  const warehousesOptions = useMemo(() => warehousesResponse?.data?.map((warehouse) => ({ label: warehouse.warehouseId, value: warehouse.warehouseId })), [warehousesResponse]);

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  // const handleSearch = useDebouncedCallback((value: string | undefined) => {
  //   _setOffset(0);
  //   _setInputValue(value);
  // }, 600);

  const handleClear = (): void => {
    _setInputValue(undefined);
    _setOffset(0);
    setFieldValue('warehouseId' as keyof SalesOrderPayloadModel, null);
  };

  // const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
  //   const target = event.currentTarget;

  //   if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && warehousesResponse && warehousesResponse?.totalCount - offset > 25) {
  //     _setOffset((prevState) => prevState + 25);
  //   }
  // };

  const handleSelect = (value: string): void => {
    setFieldValue('warehouseId' as keyof SalesOrderPayloadModel, value);
    _setInputValue(undefined);
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if ((isLoading || isFetching) && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      (isLoading || isFetching) && offset ? (
        <div style={{ padding: '0px 10px' }}>
          <Space>
            <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} size="small" />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ****************** Render ****************** */
  return (
    <>
      <Title level={5} style={styles.titleColor}>
        {CreateSalesOrderType.WAREHOUSE}
      </Title>
      <Select
        value={warehouseId ?? undefined}
        allowClear
        disabled={!siteId || isPosted}
        dropdownRender={dropDownRender}
        // filterOption={false}
        filterOption={(input, option): boolean => (option?.label as unknown as string).toLowerCase().includes(input.toLowerCase())}
        loading={isLoading || isFetching}
        onClear={handleClear}
        // onPopupScroll={handleScroll}
        // onSearch={handleSearch}
        onSelect={handleSelect}
        options={warehousesOptions}
        showSearch
        style={styles.select}
      />
    </>
  );
};
